<template>
  <f7-card class="padding-vertical-half padding-right tarjeta-vacaciones" v-if="!error403">
    <f7-card-header>
      <span>{{ $t('vacaciones.titulo') }}</span>
      <!-- Mientras no haya ficha cargada no se sabe el estado: poner «Correcto» por defecto
           decía que la configuración de vacaciones está validada aunque la carga hubiera
           fallado, y el aviso de error se cierra pero la etiqueta verde se queda. -->
      <span class="etiqueta-estado" :class="'estado-' + claveEstado">
        {{ $t('vacaciones.estados.' + claveEstado) }}
      </span>
      <!-- Deshabilitado mientras lo que se ve no sea del usuario actual: al cambiar el username
           del empleado la carga tarda, y pulsar en ese hueco guardaba los datos del anterior
           en la ficha del nuevo. [revisión #1711] `cargando` no existía en el componente, así
           que esa mitad de la condición nunca hacía nada; quien tapa el hueco es `cargadaDe`,
           que ahora además se vacía al empezar cada carga. -->
      <f7-button
        v-if="ficha.editable"
        fill
        small
        icon-f7="floppy_disk"
        :text="$t('vacaciones.guardar')"
        class="display-flex"
        :disabled="cargadaDe != username"
        @click="guardar"
      ></f7-button>
    </f7-card-header>
    <!-- [revisión #1711] Los tres estados van encadenados. Con el «Cargando…» en un v-if suelto,
         el v-else de abajo colgaba solo del error: mientras la carga viajaba se pintaban a la vez
         el aviso y el formulario con la ficha del empleado anterior. -->
    <f7-card-content v-if="errorCarga">
      <div class="notas-vacaciones">
        <p class="aviso">{{ $t('vacaciones.errorCargando') }}</p>
      </div>
    </f7-card-content>
    <f7-card-content v-else-if="!cargadaDe">
      <p class="notas-vacaciones nota-gris">{{ $t('vacaciones.cargando') }}</p>
    </f7-card-content>
    <f7-card-content v-else>
      <r-form ref="rform" v-slot="{ rform }" :initialData="formulario" :readonly="!ficha.editable">
        <f7-row no-gap class="list list-form no-hairlines">
          <f7-col width="100" medium="50">
            <ul>
              <r-input
                :label="$t('vacaciones.gestiona')"
                :form="rform"
                type="check"
                name="gestionaVacaciones"
              ></r-input>
              <r-autocomplete
                outline
                floating-label
                :dbAdapter="Empleado"
                :label="$t('vacaciones.mismaPersonaQue')"
                :form="rform"
                name="usuarioPrincipal"
                idProp="username"
                labelProp="nombre"
                prefixProp="username"
                :filter="{ filter: { activo: true } }"
              ></r-autocomplete>
              <r-autocomplete
                outline
                floating-label
                :dbAdapter="Empleado"
                :label="$t('vacaciones.aprobador')"
                :form="rform"
                name="aprobador"
                idProp="username"
                labelProp="nombre"
                prefixProp="username"
                :filter="{ filter: { activo: true, rolesExcluidos: ['CLIENTE'] } }"
              ></r-autocomplete>
              <r-autocomplete
                outline
                floating-label
                :dbAdapter="Empleado"
                :label="$t('vacaciones.suplente')"
                :form="rform"
                name="aprobadorSuplente"
                idProp="username"
                labelProp="nombre"
                prefixProp="username"
                :filter="{ filter: { activo: true, rolesExcluidos: ['CLIENTE'] } }"
              ></r-autocomplete>
            </ul>
          </f7-col>
          <f7-col width="100" medium="50">
            <ul>
              <!-- El filtro va plano: a diferencia de r-autocomplete, r-autocomplete-m2m ya
                   envuelve esta propiedad en `{ filter: … }` al llamar al buscador. -->
              <r-autocomplete-m2m
                outline
                floating-label
                :dbAdapter="Empleado"
                :label="$t('vacaciones.apruebaA')"
                :form="rform"
                name="apruebaA"
                idProp="username"
                labelProp="nombre"
                prefixProp="username"
                type="textarea"
                :lineBreak="true"
                :viewTooltip="false"
                :filter="{ activo: true }"
              ></r-autocomplete-m2m>
              <r-input
                outline
                floating-label
                class="col-50"
                :label="$t('vacaciones.diasAnuales')"
                :form="rform"
                type="number"
                name="diasAnuales"
                :readonly="true"
                :overrideReadonly="true"
              ></r-input>
              <r-input
                outline
                floating-label
                :label="$t('vacaciones.motivo')"
                :form="rform"
                type="text"
                name="motivo"
              ></r-input>
            </ul>
          </f7-col>
        </f7-row>
      </r-form>

      <div class="notas-vacaciones">
        <p v-if="ficha.cuspide">
          <b>{{ $t('vacaciones.cuspide') }}</b> {{ $t('vacaciones.cuspideNota') }}
        </p>
        <p v-for="vinculado in ficha.vinculados" :key="vinculado.username">
          {{ $t('vacaciones.mismaPersona') }}
          <b>{{ vinculado.username }}</b>
          ({{ vinculado.rol }}, {{ vinculado.gestionaVacaciones ? $t('vacaciones.siGestiona') : $t('vacaciones.noGestiona') }})
        </p>
        <p v-if="ficha.usuariosMismoDni && ficha.usuariosMismoDni.length" class="aviso">
          {{ $t('vacaciones.avisoMismoDni') }} <b>{{ ficha.usuariosMismoDni.join(', ') }}</b>
        </p>
        <p v-if="ficha.saldoDias == null" class="nota-gris">{{ $t('vacaciones.saldoPendiente') }}</p>
        <!-- En rojo cuando es negativo (#70): el saldo puede quedar por debajo de cero cuando RRHH
             registra vacaciones ya disfrutadas en nombre de alguien, y en gris pasaría por un
             saldo normal. -->
        <p v-else class="nota-gris" :class="{ 'saldo-negativo': ficha.saldoDias < 0 }">
          {{ $t('vacaciones.saldo') }}: {{ ficha.saldoDias }}
        </p>
        <p class="nota-gris">{{ $t('vacaciones.pie') }}</p>
      </div>
    </f7-card-content>
  </f7-card>
</template>
<style scoped>
.tarjeta-vacaciones .card-header {
  font-weight: bold;
  color: #22355b;
}
.etiqueta-estado {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
}
/* CARGANDO y NO_DISPONIBLE se quedan con el gris de .etiqueta-estado: sin ficha no hay color
   que afirmar. */
.estado-CORRECTO { background-color: #4cd964; }
.estado-SIN_APROBADOR,
.estado-SE_APRUEBA_A_SI_MISMO { background-color: #d32f2f; }
.estado-APROBADOR_INACTIVO { background-color: #ff9500; }
.estado-CUSPIDE { background-color: #22355b; }
.estado-NO_GESTIONA_VACACIONES { background-color: #8e8e93; }
.notas-vacaciones {
  margin: 4px 16px 8px;
  font-size: 13px;
}
.notas-vacaciones .aviso { color: #ff9500; font-weight: bold; }
.notas-vacaciones .nota-gris { color: #8e8e93; }
/* El selector completo porque `.nota-gris` ya fija el gris con el mismo peso y gana el último
   declarado: así el rojo no depende del orden de las reglas. */
.notas-vacaciones .nota-gris.saldo-negativo { color: #d32f2f; font-weight: bold; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import rAutocompleteM2m from "./../../components/rAutocompleteM2m.vue";
import Empleado from "./../../js/db/Empleado.js";
import RhVacaciones from "./../../js/db/RhVacaciones.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

/**
 * Tarjeta «Vacaciones» de la ficha del empleado (#52). Vive fuera del r-form del empleado y
 * guarda por su cuenta: lo que hay aquí no está en EmpleadoMO (decisión I5), así que guardar la
 * ficha o sincronizar con SAP no puede tocarlo.
 */
export default {
  components: {
    rForm,
    rInput,
    rAutocomplete,
    rAutocompleteM2m
  },
  props: {
    username: {
      type: String,
      required: true
    }
  },
  data: function() {
    return {
      Empleado,
      ficha: {},
      formulario: {},
      // De quién es la ficha que está pintada ahora mismo. Null mientras se carga: lo que se ve
      // todavía es del usuario anterior y no se puede guardar.
      cargadaDe: null,
      error403: false,
      // La carga falló por algo que no es un 403, o no trajo ficha. No se enseña estado: no se
      // tiene, y decir «Correcto» sin datos es afirmar que está validada.
      errorCarga: false,
      // Turno de la última carga pedida, como en Reglas de coincidencia. Solo la que lo tiene
      // pinta; las anteriores salen sin tocar nada aunque lleguen después.
      turnoCarga: 0
    };
  },
  computed: {
    claveEstado: function() {
      if (this.errorCarga) return 'NO_DISPONIBLE';
      if (!this.cargadaDe) return 'CARGANDO';
      return this.ficha.estado || 'CORRECTO';
    }
  },
  watch: {
    username: function() {
      // El 403 era de la persona anterior, y aquí esconde la tarjeta ENTERA (`v-if="!error403"`):
      // sin borrarlo, pasar por una ficha que no se puede ver dejaba la tarjeta invisible para
      // todas las siguientes. Mismo olvido que tenía la tarjeta de funciones (#51).
      this.error403 = false;
      this.cargar();
    }
  },
  created: function() {
    this.cargar();
  },
  methods: {
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    /** El formulario trabaja con usernames sueltos; la respuesta trae objetos con nombre y rol. */
    aFormulario: function(ficha) {
      return {
        gestionaVacaciones: ficha.gestionaVacaciones === true,
        usuarioPrincipal: ficha.usuarioPrincipal || null,
        aprobador: ficha.aprobador || null,
        aprobadorSuplente: ficha.aprobadorSuplente || null,
        apruebaA: (ficha.apruebaA || []).map(function(p) { return p.username; }),
        diasAnuales: ficha.diasAnuales,
        motivo: null
      };
    },
    /**
     * Mientras se recarga —el caso real es cambiar el username del empleado y guardar— la tarjeta
     * se vacía y se queda sin botón: lo que hay en pantalla es del usuario anterior, y guardarlo
     * escribiría la configuración de uno en la ficha del otro (revisión CODEX del PR #1707).
     */
    cargar: function() {
      var self = this;
      if (!self.username) return;
      // De quién es la carga que se pide y en qué turno. Si mientras viaja cambia el usuario de
      // la tarjeta —al guardar un cambio de username, por ejemplo—, la respuesta de la anterior
      // pintaría la ficha de otro y `guardar()` mandaría esos datos al endpoint del nuevo.
      //
      // El turno hace falta además del nombre: con A→B→A la carga vieja de A vuelve a coincidir
      // con la prop, así que solo comparando el username se aceptaba, y si llegaba después de la
      // nueva dejaba en el formulario una foto anterior que luego se guardaba.
      const pedido = self.username;
      const turno = ++self.turnoCarga;
      self.error403 = false;
      self.errorCarga = false;
      self.cargadaDe = null;
      // [revisión #1711] Y se vacían también la ficha y el formulario. Dejarlos puestos hacía
      // que, al cambiar de empleado, siguieran a la vista —y editables si la ficha anterior lo
      // era— los datos del anterior hasta que llegara la respuesta nueva.
      self.ficha = {};
      self.formulario = {};
      RhVacaciones.getFicha(pedido)
        .then(function(res) {
          if (turno != self.turnoCarga || pedido != self.username) return;
          // Una respuesta vacía no es una ficha: sin `username` no hay estado que enseñar, y
          // pintar la tarjeta con `{}` la dejaría en verde diciendo que está correcta.
          if (!res || !res.username) {
            self.errorCarga = true;
            return;
          }
          self.ficha = res;
          self.formulario = self.aFormulario(self.ficha);
          self.cargadaDe = pedido;
          if (self.$refs.rform) {
            self.$refs.rform.formData = self.formulario;
          }
        })
        .catch(function(e) {
          if (turno != self.turnoCarga || pedido != self.username) return;
          // Sin permiso para ver la tarjeta de otro: no se pinta nada, no se molesta con un error.
          if (self.es403(e)) {
            self.error403 = true;
          } else {
            self.errorCarga = true;
            self.$f7.dialog.alert(e && e.message ? e.message : self.$t('vacaciones.errorCargando'));
          }
        });
    },
    guardar: function() {
      var self = this;
      var app = self.$f7;
      if (!self.$refs.rform) return;
      // Se guarda sobre el usuario cuya ficha está pintada, no sobre el de la prop: si el
      // empleado ha cambiado de username y la carga nueva sigue en camino, lo que hay en el
      // formulario es del anterior y mandarlo al nuevo pisaría la ficha equivocada.
      const destino = self.cargadaDe;
      if (!destino || destino != self.username) return;
      var data = JSON.parse(JSON.stringify(self.$refs.rform.formData));
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que la apagaba.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhVacaciones.guardarFicha(destino, data)
        .then(function(res) {
          // Cambió de empleado mientras el guardado viajaba: su respuesta es de otro y pintarla
          // aquí dejaría en pantalla una ficha que no es la que se está mirando.
          if (destino != self.username) return;
          if (!res || !res.username) {
            // Guardado, pero sin ficha de vuelta: se recarga en vez de pintar un estado que no
            // se tiene.
            self.cargar();
          } else {
            // Se le retira el turno a la carga que estuviera en vuelo: es de antes de guardar,
            // así que si llegara después pintaría la ficha sin el cambio recién escrito.
            self.turnoCarga++;
            self.ficha = res;
            self.formulario = self.aFormulario(self.ficha);
            self.cargadaDe = destino;
            if (self.$refs.rform) {
              self.$refs.rform.formData = self.formulario;
            }
          }
          // El check «Gestiona vacaciones en este usuario» decide en qué usuario se gestionan las
          // funciones de esta persona, y moverlo las traslada con él (#51). Sin avisar, la tarjeta
          // «Puesto y funciones» se quedaría enseñando las de antes y con los botones de la cuenta
          // equivocada hasta recargar la página.
          self.$emit('guardada');
          app.toast
            .create({
              text: self.$t('vacaciones.guardado'),
              position: 'center',
              closeTimeout: 2000,
              icon: '<i class="f7-icons">checkmark_alt</i>'
            })
            .open();
        })
        .catch(function(e) {
          if (destino != self.username) return;
          // El backend explica el conflicto (se aprueba a sí mismo, vínculo en cadena…): se
          // enseña tal cual, que es lo que dice qué hay que corregir.
          app.dialog.alert(e && e.message ? e.message : self.$t('vacaciones.errorGuardando'));
        })
        .then(function() {
          espera.cerrar();
        });
    }
  }
};
</script>
